import { AscendonControllerConfiguration } from './src/interfaces/AscendonControllerConfiguration';
import { EventParams } from './src/interfaces/EventParams';
import './src/styles/ascendonController.scss';

const defaultSettings = {
  container: 'ascendon',
  iframeUrl: '/react/index.html',
  defaultRoute: '/',
  language: 'en-US',
  session: '',
  isGuestMode: false
};
let configSettings = {
  ...defaultSettings
};

let containerElement;
let clientComponentContainer;
let clientContainerId;
const ascendonIframeId = 'ascendon-iframe';

function elementIsReady(selector) {
  // eslint-disable-next-line consistent-return
  return new Promise((resolve) => {
    if (selector) {
      return resolve(selector);
    }
    const observer = new MutationObserver(() => {
      if (selector) {
        observer.disconnect();
        resolve(selector);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
}

export function getConfig() {
  return configSettings;
}

export function setConfig(updatedSettings: Partial<AscendonControllerConfiguration>) {
  const newSettings = { ...configSettings, ...updatedSettings };
  configSettings = newSettings;
  return configSettings;
}
function showDate() {
  const d = new Date(); // for now
  return `${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
}
function logMessage(eventName, eventDetail) {
  return `<div class="row">
            <div class="col-sm-12">
              <p class="logEvent"><span class="eventTime">${showDate()}</span> - <b>${eventName}</b> - <span class="eventDetail">${JSON.stringify(
                eventDetail
              )}<span></p>
            </div>
          </div>`;
}
function appendEventToLogs(eventName, eventDetail) {
  const logContainer = document.getElementById('eventContainer');
  if (logContainer) {
    logContainer?.insertAdjacentHTML('beforeend', logMessage(eventName, eventDetail));
    logContainer.scrollTop = logContainer.scrollHeight;
  }
}
export function getContainerElement(clientParentCompId, clientChildCompId) {
  if (clientParentCompId) {
    // Client implementation
    containerElement = document.querySelector(clientParentCompId)?.shadowRoot?.getElementById(clientChildCompId);
  } else {
    // Test harness
    containerElement = document.getElementById(clientChildCompId);
  }
  return containerElement;
}
function getIframeComponent() {
  return containerElement && containerElement.querySelector('#ascendon-iframe');
}
export function events(eventName: string, callback?: (...optionalParams: unknown[]) => void) {
  const container = getContainerElement(clientComponentContainer, clientContainerId);
  elementIsReady(container).then((containerEle: any) => {
    if (containerEle) {
      containerEle.addEventListener(
        eventName,
        function (e: CustomEvent) {
          const params = e?.detail ? e.detail : {};
          appendEventToLogs(eventName, params);
          if (callback) {
            callback(params);
          }
        },
        false
      );
    }
  });
}

export function setSession(session: object) {
  const message = {
    type: 'parentSessionEvent',
    param: { session }
  };
  const iframeElement = getIframeComponent();
  if (iframeElement?.contentWindow) {
    iframeElement.contentWindow.postMessage(JSON.stringify(message), configSettings.iframeUrl);
  }
}

export function event(name: string, params?: EventParams) {
  const formattedParams = {
    action: name,
    data: JSON.stringify(params)
  };
  const message = {
    type: 'eventManagerEvent',
    name,
    params: formattedParams
  };
  window.postMessage(JSON.stringify(message), window.location.href);
}

export function childEvent(name: string, params?: EventParams) {
  const message = {
    type: 'parentEvent',
    name,
    params
  };
  const iframeElement = getIframeComponent();
  if (iframeElement?.contentWindow) {
    iframeElement.contentWindow.postMessage(JSON.stringify(message), configSettings.iframeUrl);
  }
}

export function route(name: string, param?: EventParams) {
  const message = {
    type: 'route',
    name,
    param
  };
  const iframeElement = getIframeComponent();
  if (iframeElement?.contentWindow) {
    iframeElement.contentWindow.postMessage(JSON.stringify(message), configSettings.iframeUrl);
  }
}

const receiveMessage = (e: MessageEvent) => {
  const options = getConfig();
  if (
    [options.iframeUrl, window.location.origin].some((acceptedUrl) => e.origin.includes(acceptedUrl)) &&
    typeof e.data !== 'undefined'
  ) {
    try {
      if (typeof e.data === 'string') {
        let message;
        try {
          message = e.data.indexOf('[iFrameSizer]') < 0 ? JSON.parse(e.data) : {};
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(`Ignoring unparsable message `, e, error);
        }
        if (message?.type === 'eventManagerEvent') {
          if (message.params.action === 'module.loaded') {
            // @ts-expect-error
            window.iFrameResize(
              {
                log: false,
                checkOrigin: false
              },
              getIframeComponent()
            );

            //Initialize to default route
            route(options.defaultRoute || configSettings.defaultRoute, {
              initialLoad: true,
              language: configSettings.language,
              session: options.session,
              isGuestMode: configSettings.isGuestMode
            });
            event('module.ready');
          } else {
            let data = {};
            try {
              data = JSON.parse(message.params.data);
            } catch (error) {
              data = message.params;
            }
            const customEvent = new CustomEvent(message.params.action, {
              detail: data
            });
            containerElement.dispatchEvent(customEvent);
          }
        }
      }
    } catch (err) {
      return false;
    }
  }
  return false;
};

export function init(options: {
  iframeUrl: string;
  container?: string;
  defaultRoute?: string;
  session?: string;
  language?: string;
  componentContainer?: string;
  isGuestMode?: boolean;
}) {
  // Update default config settings
  setConfig(options);
  // Reference Important DOM Elements
  clientComponentContainer = options?.componentContainer;
  clientContainerId = options?.container || configSettings.container;
  containerElement = getContainerElement(clientComponentContainer, clientContainerId);

  if (containerElement) {
    // Listen to post messages on the page (events or route changes)
    window.addEventListener('message', receiveMessage, false);
    //Create Iframe on page and attach to DOM container
    const iframe = document.createElement('iframe');
    iframe.id = ascendonIframeId;
    iframe.src = configSettings.iframeUrl;
    iframe.style.overflow = 'hidden';
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    //Create dynamic div element on page to attach our UI widgets (modals, loaders etc...)
    containerElement.append(iframe);
    // if iFrameResizer doesn't exist, attach it to the window
    // @ts-expect-error
    if (!window.iFrameResize) {
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.1/iframeResizer.min.js';
      window.document.body.appendChild(s);
    }
  }
  return configSettings;
}

export function removePostMessageListener() {
  window.removeEventListener('message', receiveMessage, false);
}

export function getPayment() {
  childEvent('module.getPayment-init', {
    data: {}
  });
}
